import { useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';

const ALLOWED_EXT = ['xlsx', 'xls'];

const fmtSize = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const BulkUploadModal = ({
    title,
    description,
    templateName,
    templateHeaders,
    onDownloadTemplate,
    templateSample,
    templateNotes,
    onClose,
    onUpload,
    uploading,
    result,
}) => {
    const [file, setFile] = useState(null);
    const [fileError, setFileError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const inputRef = useRef(null);

    const handleFile = (f) => {
        if (!f) return;
        const ext = f.name.split('.').pop().toLowerCase();
        if (!ALLOWED_EXT.includes(ext)) {
            setFileError('Only Excel files (.xls, .xlsx) are supported.');
            setFile(null);
            return;
        }
        setFileError(null);
        setFile(f);
    };

    const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files[0]); };
    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = () => setIsDragging(false);
    const removeFile = () => { setFile(null); setFileError(null); if (inputRef.current) inputRef.current.value = ''; };

    // A caller can hand over the download (a server-built sheet with real
     // dropdowns, which SheetJS cannot write); otherwise the sheet is built here.
    const downloadTemplate = () => {
        if (typeof onDownloadTemplate === 'function') {
            onDownloadTemplate();
            return;
        }
        const rows = [templateHeaders, templateSample];
        if (templateNotes) rows.push(templateNotes);
        const ws = XLSX.utils.aoa_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        XLSX.writeFile(wb, templateName);
    };

    const handleSubmit = () => {
        if (file && !uploading) onUpload(file);
    };

    const isSuccess = result?.success === true;
    const summary = result?.data || result;
    const inserted = summary?.inserted ?? summary?.success_count ?? summary?.successCount;
    const errorRows = summary?.errors || summary?.error_rows || [];
    // The wings/flats endpoints report rejected rows as an errors array and
    // send no count field, which left the Failed tile blank on a partial
    // upload. Fall back to the array length.
    const failed = summary?.failed ?? summary?.error_count ?? summary?.errorCount
        ?? (Array.isArray(errorRows) ? errorRows.length : undefined);
    // Rows the API accepted but wants an admin to look at - employees whose
    // access role was defaulted because the sheet left the column blank.
    const needsAccessRole = summary?.needsAccessRole || summary?.needs_access_role || [];

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4" onClick={onClose}>
            <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-max-w-xl tw-w-full tw-max-h-[90vh] tw-overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-emerald-50 tw-text-emerald-600 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:file-excel-outline" className="tw-text-lg" />
                        </div>
                        <div>
                            <h3 className="tw-font-semibold tw-text-gray-800 tw-text-sm">{title}</h3>
                            <p className="tw-text-[11px] tw-text-gray-500">{description}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="tw-p-1 tw-rounded-full hover:tw-bg-gray-100">
                        <Icon icon="mdi:close" className="tw-text-base tw-text-gray-400" />
                    </button>
                </div>

                <div className="tw-p-5 tw-overflow-y-auto" style={{ maxHeight: 'calc(90vh - 130px)' }}>
                    {/* Step 1: download template hint */}
                    {!result && (
                        <>
                            <button
                                type="button"
                                onClick={downloadTemplate}
                                className="tw-w-full tw-mb-4 tw-px-4 tw-py-2.5 tw-rounded-lg tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-text-emerald-700 tw-text-xs tw-font-medium tw-flex tw-items-center tw-justify-center tw-gap-2 hover:tw-bg-emerald-100"
                            >
                                <Icon icon="mdi:download" />
                                Download Sample Template
                            </button>

                            {/* Drop zone */}
                            <div
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onClick={() => inputRef.current?.click()}
                                className={`tw-border-2 tw-border-dashed tw-rounded-lg tw-p-6 tw-text-center tw-cursor-pointer tw-transition-colors ${isDragging ? 'tw-border-emerald-500 tw-bg-emerald-50' : 'tw-border-gray-200 hover:tw-border-emerald-400 hover:tw-bg-gray-50'}`}
                            >
                                {file ? (
                                    <div className="tw-flex tw-items-center tw-justify-between">
                                        <div className="tw-flex tw-items-center tw-gap-3 tw-text-left tw-min-w-0">
                                            <Icon icon="mdi:file-excel-outline" className="tw-text-2xl tw-text-emerald-600 tw-flex-shrink-0" />
                                            <div className="tw-min-w-0">
                                                <p className="tw-text-xs tw-font-semibold tw-text-gray-800 tw-truncate">{file.name}</p>
                                                <p className="tw-text-[10px] tw-text-gray-500">{fmtSize(file.size)}</p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); removeFile(); }}
                                            className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-600"
                                        >
                                            <Icon icon="mdi:close" />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <Icon icon="mdi:cloud-upload-outline" className="tw-text-3xl tw-text-gray-400 tw-mb-2 tw-block tw-mx-auto" />
                                        <p className="tw-text-xs tw-text-gray-600 tw-font-medium">Drop your Excel file here or <span className="tw-text-emerald-600">browse</span></p>
                                        <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1">.xlsx or .xls (max 5MB)</p>
                                    </>
                                )}
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    ref={inputRef}
                                    style={{ display: 'none' }}
                                    onChange={(e) => handleFile(e.target.files?.[0])}
                                />
                            </div>
                            {fileError && (
                                <p className="tw-text-[11px] tw-text-rose-600 tw-mt-2">
                                    <Icon icon="mdi:alert-circle-outline" className="tw-inline tw-mr-1" />{fileError}
                                </p>
                            )}
                        </>
                    )}

                    {/* Result */}
                    {result && (
                        <div className={`tw-rounded-lg tw-p-4 tw-border ${isSuccess ? 'tw-bg-emerald-50 tw-border-emerald-100' : 'tw-bg-rose-50 tw-border-rose-100'}`}>
                            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                                <Icon icon={isSuccess ? 'mdi:check-circle-outline' : 'mdi:alert-circle-outline'} className={`tw-text-xl ${isSuccess ? 'tw-text-emerald-600' : 'tw-text-rose-600'}`} />
                                <h4 className="tw-text-sm tw-font-semibold tw-text-gray-800">
                                    {isSuccess ? 'Upload completed' : 'Upload failed'}
                                </h4>
                            </div>
                            {result.message && <p className="tw-text-xs tw-text-gray-700 tw-mb-2">{result.message}</p>}
                            {(inserted != null || failed != null) && (
                                <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-mb-2">
                                    {inserted != null && (
                                        <div className="tw-bg-white tw-rounded tw-p-2 tw-border tw-border-gray-100">
                                            <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase">Inserted</p>
                                            <p className="tw-text-lg tw-font-bold tw-text-emerald-700">{inserted}</p>
                                        </div>
                                    )}
                                    {failed != null && (
                                        <div className="tw-bg-white tw-rounded tw-p-2 tw-border tw-border-gray-100">
                                            <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase">Failed</p>
                                            <p className="tw-text-lg tw-font-bold tw-text-rose-700">{failed}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                            {Array.isArray(needsAccessRole) && needsAccessRole.length > 0 && (
                                <div className="tw-mt-3 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded tw-p-3">
                                    <div className="tw-flex tw-items-start tw-gap-2">
                                        <Icon icon="mdi:shield-alert-outline" className="tw-text-amber-600 tw-text-base tw-flex-shrink-0 tw-mt-0.5" />
                                        <div className="tw-min-w-0">
                                            <p className="tw-text-[11px] tw-font-semibold tw-text-amber-800">
                                                Set the access role for {needsAccessRole.length} employee{needsAccessRole.length > 1 ? 's' : ''}
                                            </p>
                                            <p className="tw-text-[11px] tw-text-amber-700 tw-mb-2">
                                                These rows left the Access Role column blank, so each employee was given the
                                                default role for their employee type. Open Employees &gt; Edit and set the right
                                                access role.
                                            </p>
                                            <div className="tw-bg-white tw-rounded tw-border tw-border-amber-100 tw-max-h-40 tw-overflow-y-auto">
                                                <table className="tw-w-full tw-text-xs">
                                                    <thead>
                                                        <tr className="tw-bg-amber-50/60">
                                                            <th className="tw-text-left tw-px-2 tw-py-1 tw-text-[10px] tw-text-amber-800">Row</th>
                                                            <th className="tw-text-left tw-px-2 tw-py-1 tw-text-[10px] tw-text-amber-800">Employee</th>
                                                            <th className="tw-text-left tw-px-2 tw-py-1 tw-text-[10px] tw-text-amber-800">Given role</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {needsAccessRole.map((r, i) => (
                                                            <tr key={i} className="tw-border-t tw-border-amber-50">
                                                                <td className="tw-px-2 tw-py-1 tw-text-gray-700">{r.row ?? i + 1}</td>
                                                                <td className="tw-px-2 tw-py-1 tw-text-gray-700">
                                                                    {r.name || r.email || '-'}
                                                                    {r.employee_type ? <span className="tw-text-gray-400"> · {r.employee_type}</span> : null}
                                                                </td>
                                                                <td className="tw-px-2 tw-py-1 tw-text-amber-800">{r.default_role || '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {Array.isArray(errorRows) && errorRows.length > 0 && (
                                <div className="tw-mt-3">
                                    <p className="tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-mb-1">Errors</p>
                                    <div className="tw-bg-white tw-rounded tw-border tw-border-gray-100 tw-max-h-40 tw-overflow-y-auto">
                                        <table className="tw-w-full tw-text-xs">
                                            <thead>
                                                <tr className="tw-bg-gray-50">
                                                    <th className="tw-text-left tw-px-2 tw-py-1 tw-text-[10px] tw-text-gray-600">Row</th>
                                                    <th className="tw-text-left tw-px-2 tw-py-1 tw-text-[10px] tw-text-gray-600">Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {errorRows.map((er, i) => (
                                                    <tr key={i} className="tw-border-t tw-border-gray-50">
                                                        <td className="tw-px-2 tw-py-1 tw-text-gray-700">{er.row ?? er.line ?? i + 1}</td>
                                                        <td className="tw-px-2 tw-py-1 tw-text-rose-700">{er.error || er.message || JSON.stringify(er)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="tw-p-3 tw-border-t tw-border-gray-100 tw-flex tw-justify-end tw-gap-2 tw-bg-gray-50/50">
                    <button onClick={onClose} className="tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-white">
                        {result ? 'Close' : 'Cancel'}
                    </button>
                    {!result && (
                        <button
                            onClick={handleSubmit}
                            disabled={!file || uploading}
                            className="tw-px-4 tw-py-1.5 tw-bg-emerald-600 tw-text-white tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-emerald-700 disabled:tw-opacity-50 tw-flex tw-items-center tw-gap-2"
                        >
                            {uploading && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent" />}
                            <Icon icon="mdi:upload" />
                            Upload
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BulkUploadModal;
